import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText, Line, Rect, Path } from 'react-native-svg';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const AdvanceTaxCalculatorScreen = () => {
  const { colors } = useTheme();
  const [salaryIncome, setSalaryIncome] = useState('1200000');
  const [businessIncome, setBusinessIncome] = useState('0');
  const [capitalGains, setCapitalGains] = useState('0');
  const [otherIncome, setOtherIncome] = useState('0');
  const [deductions80C, setDeductions80C] = useState('150000');
  const [otherDeductions, setOtherDeductions] = useState('0');
  const [previousYearTax, setPreviousYearTax] = useState('0');
  const [tdsDeducted, setTdsDeducted] = useState('0');
  const [age, setAge] = useState('below60');
  const [financialYear, setFinancialYear] = useState('2024-25');
  const [showPaymentSchedule, setShowPaymentSchedule] = useState(false);
  const [showPenalties, setShowPenalties] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);

  const ageCategories = [
    { value: 'below60', label: 'Below 60 years', exemption: 250000 },
    { value: '60to80', label: '60-80 years', exemption: 300000 },
    { value: 'above80', label: 'Above 80 years', exemption: 500000 },
  ];

  const financialYears = [
    { value: '2024-25', label: 'FY 2024-25', ay: 'AY 2025-26' },
    { value: '2023-24', label: 'FY 2023-24', ay: 'AY 2024-25' },
  ];

  // Tax slabs for FY 2024-25 (New Regime)
  const taxSlabs = [
    { min: 0, max: 300000, rate: 0 },
    { min: 300000, max: 700000, rate: 5 },
    { min: 700000, max: 1000000, rate: 10 },
    { min: 1000000, max: 1200000, rate: 15 },
    { min: 1200000, max: 1500000, rate: 20 },
    { min: 1500000, max: Infinity, rate: 30 },
  ];

  // Advance tax payment schedule
  const paymentSchedule = [
    { installment: 1, dueDate: '15-Jun-2024', percentage: 15, description: 'First Installment' },
    { installment: 2, dueDate: '15-Sep-2024', percentage: 45, description: 'Second Installment (Cumulative 45%)' },
    { installment: 3, dueDate: '15-Dec-2024', percentage: 75, description: 'Third Installment (Cumulative 75%)' },
    { installment: 4, dueDate: '15-Mar-2025', percentage: 100, description: 'Fourth Installment (Remaining 25%)' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const calculateTax = (taxableIncome, ageCategory) => {
    const selectedAge = ageCategories.find(a => a.value === ageCategory);
    const exemption = selectedAge ? selectedAge.exemption : 250000;
    
    // Apply basic exemption
    const incomeAfterExemption = Math.max(0, taxableIncome - exemption);
    
    let tax = 0;
    let remainingIncome = incomeAfterExemption;

    // Calculate tax based on slabs
    for (const slab of taxSlabs) {
      if (remainingIncome <= 0) break;
      
      const slabIncome = Math.min(remainingIncome, slab.max - slab.min);
      tax += (slabIncome * slab.rate) / 100;
      remainingIncome -= slabIncome;
    }

    // Add Health and Education Cess (4%)
    const cess = tax * 0.04;
    const totalTax = tax + cess;

    return {
      incomeAfterExemption,
      baseTax: tax,
      cess,
      totalTax,
      exemption,
    };
  };

  const calculateAdvanceTax = () => {
    const salary = parseFloat(salaryIncome) || 0;
    const business = parseFloat(businessIncome) || 0;
    const capital = parseFloat(capitalGains) || 0;
    const other = parseFloat(otherIncome) || 0;
    const deduction80C = parseFloat(deductions80C) || 0;
    const otherDed = parseFloat(otherDeductions) || 0;
    const prevTax = parseFloat(previousYearTax) || 0;
    const tds = parseFloat(tdsDeducted) || 0;

    // Calculate total income
    const totalIncome = salary + business + capital + other;
    
    // Calculate total deductions
    const totalDeductions = Math.min(deduction80C, 150000) + otherDed;
    
    // Calculate taxable income
    const taxableIncome = Math.max(0, totalIncome - totalDeductions);

    // Calculate tax liability
    const taxCalculation = calculateTax(taxableIncome, age);
    const { totalTax } = taxCalculation;

    // Calculate advance tax liability
    let advanceTaxLiability = totalTax - tds;
    
    // Minimum threshold for advance tax (₹10,000)
    const isAdvanceTaxApplicable = advanceTaxLiability >= 10000;
    
    if (!isAdvanceTaxApplicable) {
      advanceTaxLiability = 0;
    }

    // Calculate quarterly payments
    const quarterlyPayments = paymentSchedule.map((schedule, index) => {
      const cumulativePercentage = schedule.percentage;
      const previousPercentage = index > 0 ? paymentSchedule[index - 1].percentage : 0;
      const installmentPercentage = cumulativePercentage - previousPercentage;
      const installmentAmount = (advanceTaxLiability * installmentPercentage) / 100;
      
      return {
        ...schedule,
        installmentPercentage,
        installmentAmount,
        cumulativeAmount: (advanceTaxLiability * cumulativePercentage) / 100,
      };
    });

    // Calculate penalties for late payment (simplified)
    const penaltyRate = 1; // 1% per month
    const penalties = quarterlyPayments.map(payment => ({
      ...payment,
      penaltyFor1Month: (payment.installmentAmount * penaltyRate) / 100,
      penaltyFor3Months: (payment.installmentAmount * penaltyRate * 3) / 100,
    }));

    // Tax breakdown by income source
    const incomeBreakdown = [
      { source: 'Salary Income', amount: salary, percentage: totalIncome > 0 ? (salary / totalIncome) * 100 : 0 },
      { source: 'Business Income', amount: business, percentage: totalIncome > 0 ? (business / totalIncome) * 100 : 0 },
      { source: 'Capital Gains', amount: capital, percentage: totalIncome > 0 ? (capital / totalIncome) * 100 : 0 },
      { source: 'Other Income', amount: other, percentage: totalIncome > 0 ? (other / totalIncome) * 100 : 0 },
    ].filter(item => item.amount > 0);

    return {
      totalIncome,
      totalDeductions,
      taxableIncome,
      ...taxCalculation,
      advanceTaxLiability,
      isAdvanceTaxApplicable,
      quarterlyPayments,
      penalties,
      incomeBreakdown,
      tdsDeducted: tds,
      netTaxPayable: Math.max(0, totalTax - tds),
    };
  };

  useEffect(() => {
    const calculatedResults = calculateAdvanceTax();
    setResults(calculatedResults);
  }, [salaryIncome, businessIncome, capitalGains, otherIncome, deductions80C, otherDeductions, tdsDeducted, age]);

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>Advanced Tax Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate your advance tax liability and quarterly payment schedule with penalty calculations
      </Text>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>Income & Tax Details</Text>
      
      {/* Age Category */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Age Category</Text>
        <View style={styles.ageContainer}>
          {ageCategories.map((category) => (
            <TouchableOpacity
              key={category.value}
              style={[
                styles.ageButton,
                age === category.value && styles.ageButtonActive,
              ]}
              onPress={() => setAge(category.value)}
            >
              <Text style={[
                styles.ageLabel,
                age === category.value && styles.ageLabelActive,
              ]}>
                {category.label}
              </Text>
              <Text style={[
                styles.ageExemption,
                age === category.value && styles.ageExemptionActive,
              ]}>
                Exemption: {formatCurrency(category.exemption)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Financial Year */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Financial Year</Text>
        <View style={styles.fyContainer}>
          {financialYears.map((fy) => (
            <TouchableOpacity
              key={fy.value}
              style={[
                styles.fyButton,
                financialYear === fy.value && styles.fyButtonActive,
              ]}
              onPress={() => setFinancialYear(fy.value)}
            >
              <Text style={[
                styles.fyLabel,
                financialYear === fy.value && styles.fyLabelActive,
              ]}>
                {fy.label}
              </Text>
              <Text style={[
                styles.fyAY,
                financialYear === fy.value && styles.fyAYActive,
              ]}>
                {fy.ay}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Income Sources */}
      <Text style={styles.sectionTitle}>Income Sources</Text>
      
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Salary Income</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={salaryIncome}
            onChangeText={setSalaryIncome}
            placeholder="1200000"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Annual salary including allowances</Text>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Business/Professional Income</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={businessIncome}
            onChangeText={setBusinessIncome}
            placeholder="0"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Net business or professional income</Text>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Capital Gains</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={capitalGains}
            onChangeText={setCapitalGains}
            placeholder="0"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Short-term and long-term capital gains</Text>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Other Income</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={otherIncome}
            onChangeText={setOtherIncome}
            placeholder="0"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Interest, rental, dividend income</Text>
      </View>

      {/* Deductions */}
      <Text style={styles.sectionTitle}>Deductions</Text>
      
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Section 80C Deductions</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={deductions80C}
            onChangeText={setDeductions80C}
            placeholder="150000"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Maximum limit: ₹1,50,000</Text>
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.label}>Other Deductions</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={otherDeductions}
            onChangeText={setOtherDeductions}
            placeholder="0"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>80D, 80G, 80E, etc.</Text>
      </View>

      {/* TDS */}
      <Text style={styles.sectionTitle}>Tax Already Paid</Text>
      
      <View style={styles.inputGroup}>
        <Text style={styles.label}>TDS Deducted</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={tdsDeducted}
            onChangeText={setTdsDeducted}
            placeholder="0"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Tax deducted at source during the year</Text>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results || !results.incomeBreakdown.length) return null;

    const { incomeBreakdown } = results;
    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const colors_chart = [colors.primary, colors.success, colors.warning, colors.error];
    let cumulativePercentage = 0;

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>Income Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            {/* Background circle */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            
            {incomeBreakdown.map((item, index) => {
              const strokeDasharray = `${(item.percentage / 100) * circumference} ${circumference}`;
              const strokeDashoffset = -((cumulativePercentage / 100) * circumference);
              cumulativePercentage += item.percentage;
              
              return (
                <Circle
                  key={index}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  stroke={colors_chart[index % colors_chart.length]}
                  strokeWidth={strokeWidth}
                  fill="transparent"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
              );
            })}
            
            {/* Center text */}
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={colors.text}
            >
              Total Income
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              {formatCurrency(results.totalIncome)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          {incomeBreakdown.map((item, index) => (
            <View key={index} style={styles.legendItem}>
              <View style={[styles.legendColor, { backgroundColor: colors_chart[index % colors_chart.length] }]} />
              <Text style={styles.legendText}>
                {item.source} ({item.percentage.toFixed(1)}%)
              </Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    const {
      totalIncome,
      totalDeductions,
      taxableIncome,
      exemption,
      baseTax,
      cess,
      totalTax,
      advanceTaxLiability,
      isAdvanceTaxApplicable,
      tdsDeducted,
      netTaxPayable,
    } = results;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>Tax Calculation Summary</Text>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total Income</Text>
          <Text style={styles.resultValue}>{formatCurrency(totalIncome)}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total Deductions</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            -{formatCurrency(totalDeductions)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Basic Exemption</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            -{formatCurrency(exemption)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Taxable Income</Text>
          <Text style={styles.resultValue}>{formatCurrency(taxableIncome)}</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Income Tax</Text>
          <Text style={styles.resultValue}>{formatCurrency(baseTax)}</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Health & Education Cess (4%)</Text>
          <Text style={styles.resultValue}>{formatCurrency(cess)}</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total Tax Liability</Text>
          <Text style={[styles.resultValue, { fontSize: 18, fontWeight: 'bold' }]}>
            {formatCurrency(totalTax)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>TDS Deducted</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            -{formatCurrency(tdsDeducted)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Net Tax Payable</Text>
          <Text style={[
            styles.resultValue, 
            { 
              fontSize: 18, 
              fontWeight: 'bold',
              color: netTaxPayable > 0 ? colors.error : colors.success 
            }
          ]}>
            {formatCurrency(netTaxPayable)}
          </Text>
        </View>

        <View style={styles.advanceTaxInfo}>
          <View style={[
            styles.advanceTaxBadge,
            { backgroundColor: isAdvanceTaxApplicable ? colors.warning : colors.success }
          ]}>
            <Text style={styles.advanceTaxBadgeText}>
              {isAdvanceTaxApplicable ? 'Advance Tax Applicable' : 'No Advance Tax Required'}
            </Text>
          </View>
          
          {isAdvanceTaxApplicable && (
            <View style={styles.advanceTaxAmount}>
              <Text style={styles.advanceTaxLabel}>Advance Tax Liability</Text>
              <Text style={styles.advanceTaxValue}>
                {formatCurrency(advanceTaxLiability)}
              </Text>
            </View>
          )}
        </View>
      </View>
    );
  };

  const renderPaymentSchedule = () => {
    if (!results || !results.isAdvanceTaxApplicable) return null;

    const { quarterlyPayments } = results;

    return (
      <View style={styles.scheduleCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowPaymentSchedule(!showPaymentSchedule)}
        >
          <Text style={styles.cardTitle}>Quarterly Payment Schedule</Text>
          <Ionicons
            name={showPaymentSchedule ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showPaymentSchedule && (
          <View style={styles.cardContent}>
            {quarterlyPayments.map((payment, index) => (
              <View key={index} style={styles.paymentItem}>
                <View style={styles.paymentHeader}>
                  <Text style={styles.paymentInstallment}>
                    Installment {payment.installment}
                  </Text>
                  <Text style={styles.paymentDueDate}>
                    Due: {payment.dueDate}
                  </Text>
                </View>
                
                <Text style={styles.paymentDescription}>
                  {payment.description}
                </Text>
                
                <View style={styles.paymentDetails}>
                  <View style={styles.paymentDetailItem}>
                    <Text style={styles.paymentDetailLabel}>Installment Amount</Text>
                    <Text style={styles.paymentDetailValue}>
                      {formatCurrency(payment.installmentAmount)}
                    </Text>
                  </View>
                  
                  <View style={styles.paymentDetailItem}>
                    <Text style={styles.paymentDetailLabel}>Cumulative Amount</Text>
                    <Text style={styles.paymentDetailValue}>
                      {formatCurrency(payment.cumulativeAmount)}
                    </Text>
                  </View>
                  
                  <View style={styles.paymentDetailItem}>
                    <Text style={styles.paymentDetailLabel}>Percentage</Text>
                    <Text style={styles.paymentDetailValue}>
                      {payment.installmentPercentage}%
                    </Text>
                  </View>
                </View>
                
                <View style={styles.paymentBar}>
                  <View 
                    style={[
                      styles.paymentBarFill, 
                      { 
                        width: `${payment.percentage}%`,
                        backgroundColor: colors.primary
                      }
                    ]} 
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderPenalties = () => {
    if (!results || !results.isAdvanceTaxApplicable) return null;

    const { penalties } = results;

    return (
      <View style={styles.penaltiesCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowPenalties(!showPenalties)}
        >
          <Text style={styles.cardTitle}>Penalty Calculator</Text>
          <Ionicons
            name={showPenalties ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showPenalties && (
          <View style={styles.cardContent}>
            <Text style={styles.penaltyInfo}>
              Penalty is charged at 1% per month for late or insufficient payment of advance tax.
            </Text>
            
            {penalties.map((penalty, index) => (
              <View key={index} style={styles.penaltyItem}>
                <Text style={styles.penaltyInstallment}>
                  Installment {penalty.installment} - {penalty.dueDate}
                </Text>
                
                <View style={styles.penaltyDetails}>
                  <View style={styles.penaltyDetailItem}>
                    <Text style={styles.penaltyDetailLabel}>Installment Amount</Text>
                    <Text style={styles.penaltyDetailValue}>
                      {formatCurrency(penalty.installmentAmount)}
                    </Text>
                  </View>
                  
                  <View style={styles.penaltyDetailItem}>
                    <Text style={styles.penaltyDetailLabel}>Penalty (1 month late)</Text>
                    <Text style={[styles.penaltyDetailValue, { color: colors.warning }]}>
                      {formatCurrency(penalty.penaltyFor1Month)}
                    </Text>
                  </View>
                  
                  <View style={styles.penaltyDetailItem}>
                    <Text style={styles.penaltyDetailLabel}>Penalty (3 months late)</Text>
                    <Text style={[styles.penaltyDetailValue, { color: colors.error }]}>
                      {formatCurrency(penalty.penaltyFor3Months)}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>About Advance Tax</Text>
      <Text style={styles.infoSubtitle}>Understanding Quarterly Tax Payments</Text>
      
      <Text style={styles.infoText}>
        Advance tax is a method of paying income tax in advance during the financial year 
        instead of paying it as a lump sum at the end. It's mandatory if your tax liability 
        exceeds ₹10,000 in a financial year.
      </Text>
      
      <View style={styles.tipBox}>
        <Ionicons name="calendar" size={16} color={colors.primary} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Payment Schedule:</Text> Four installments 
          due on 15th June (15%), 15th September (30%), 15th December (30%), and 15th March (25%).
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="warning" size={16} color={colors.warning} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Penalty:</Text> 1% per month simple interest 
          is charged for late or insufficient payment of advance tax installments.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="shield-checkmark" size={16} color={colors.success} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Exemption:</Text> Salaried individuals with 
          no other income sources are generally exempt from advance tax if TDS covers their liability.
        </Text>
      </View>
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "Who needs to pay advance tax?",
        answer: "Any individual, HUF, company, or firm whose estimated tax liability for the year exceeds ₹10,000 must pay advance tax. However, senior citizens (60+ years) are exempt if they have no business income."
      },
      {
        question: "What happens if I don't pay advance tax?",
        answer: "If you fail to pay advance tax or pay less than the required amount, you'll be charged interest at 1% per month from the due date until the date of payment. This interest is calculated on simple interest basis."
      },
      {
        question: "Can I revise my advance tax payments?",
        answer: "Yes, you can revise your advance tax estimate and payments based on your actual income during the year. You can pay more in subsequent installments if your income increases, or pay less if it decreases."
      },
      {
        question: "What if I pay excess advance tax?",
        answer: "If you pay excess advance tax, the excess amount will be refunded to you after filing your income tax return, provided all other tax obligations are met. No interest is paid on excess advance tax."
      },
      {
        question: "Are salaried employees exempt from advance tax?",
        answer: "Salaried employees are generally exempt from advance tax if their employer deducts adequate TDS. However, if they have significant other income (business, capital gains, etc.), they may need to pay advance tax."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.surface,
      paddingVertical: 40,
      paddingHorizontal: 20,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 24,
    },
    content: {
      flexDirection: isMobile ? 'column' : 'row',
      padding: 20,
      gap: 20,
      width: '100%',
    },
    leftPanel: {
      flex: isMobile ? 1 : 1.2,
    },
    rightPanel: {
      flex: 1,
      gap: 20,
    },
    calculatorCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginTop: 20,
      marginBottom: 12,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      paddingLeft: 12,
    },
    textInput: {
      flex: 1,
      height: 48,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
    },
    helperText: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 4,
    },
    ageContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 8,
      marginTop: 8,
    },
    ageButton: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 12,
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
    },
    ageButtonActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    ageLabel: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
      textAlign: 'center',
    },
    ageLabelActive: {
      color: colors.background,
    },
    ageExemption: {
      fontSize: 12,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    ageExemptionActive: {
      color: colors.background,
    },
    fyContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 8,
      marginTop: 8,
    },
    fyButton: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 12,
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
    },
    fyButtonActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    fyLabel: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    fyLabelActive: {
      color: colors.background,
    },
    fyAY: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    fyAYActive: {
      color: colors.background,
    },
    chartCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    chartContainer: {
      alignItems: 'center',
      marginVertical: 20,
    },
    chartLegend: {
      marginTop: 20,
      gap: 8,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultsCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    advanceTaxInfo: {
      marginTop: 20,
      padding: 16,
      backgroundColor: colors.surface,
      borderRadius: 8,
    },
    advanceTaxBadge: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
      alignSelf: 'flex-start',
      marginBottom: 12,
    },
    advanceTaxBadgeText: {
      fontSize: 12,
      color: colors.background,
      fontWeight: 'bold',
    },
    advanceTaxAmount: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    advanceTaxLabel: {
      fontSize: 14,
      color: colors.text,
      fontWeight: '600',
    },
    advanceTaxValue: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.primary,
    },
    scheduleCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    cardContent: {
      marginTop: 20,
    },
    paymentItem: {
      marginBottom: 20,
      padding: 16,
      backgroundColor: colors.background,
      borderRadius: 8,
    },
    paymentHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    paymentInstallment: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
    },
    paymentDueDate: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: '600',
    },
    paymentDescription: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 12,
    },
    paymentDetails: {
      marginBottom: 12,
    },
    paymentDetailItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 4,
    },
    paymentDetailLabel: {
      fontSize: 13,
      color: colors.textSecondary,
    },
    paymentDetailValue: {
      fontSize: 13,
      fontWeight: '600',
      color: colors.text,
    },
    paymentBar: {
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      overflow: 'hidden',
    },
    paymentBarFill: {
      height: '100%',
      borderRadius: 2,
    },
    penaltiesCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    penaltyInfo: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 16,
      fontStyle: 'italic',
    },
    penaltyItem: {
      marginBottom: 16,
      padding: 12,
      backgroundColor: colors.background,
      borderRadius: 8,
    },
    penaltyInstallment: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    penaltyDetails: {
      gap: 4,
    },
    penaltyDetailItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    penaltyDetailLabel: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    penaltyDetailValue: {
      fontSize: 12,
      fontWeight: '600',
      color: colors.text,
    },
    infoContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    infoTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    infoSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      marginBottom: 16,
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 16,
    },
    tipBox: {
      flexDirection: 'row',
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
      marginTop: 8,
    },
    tipText: {
      fontSize: 13,
      color: colors.textSecondary,
      marginLeft: 8,
      flex: 1,
      lineHeight: 18,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      marginTop: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    fullWidthSection: {
      paddingHorizontal: 20,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      {renderPaymentSchedule()}
      {renderPenalties()}
      {renderInformation()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

export default AdvanceTaxCalculatorScreen;